import { EventTypes, NodeTree, ROOT_NODE } from '@candulabs/craft';
import React, { Fragment, ReactElement, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { colors } from 'styleguide';

import { useEventing } from 'commons/eventing';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { useEditor, useMutations, useSelection } from 'craft/hooks';
import { setPaletteTab, usePaletteAppearence, useViewportDom } from 'craft/state';
import { PaletteAppearance } from 'craft/state/menus/reducer';
import { ContentDocument } from 'craft/types';

const Container = styled.div<{ noStyles?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;

  ${({ noStyles }) =>
    !noStyles &&
    css`
      background-color: white;
      padding: 0.5em;
      border: 1px solid ${colors('neutral', 4)};
      border-radius: 0.25em;

      &:hover {
        border: 1px solid ${colors('neutral', 2)};
        box-shadow: 0 0 1px 1px ${colors('neutral', 5)};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

export interface DropInContainerProps {
  id: string;
  contentDocument?: ContentDocument;
  dropIn?: ReactElement<any, any>;
  children: ReactNode;
  noStyles?: boolean;
  onDropIn?: (nodeTree: NodeTree) => void;
  disabled?: boolean;
}

const DropInContainer = ({
  id,
  dropIn,
  contentDocument,
  children,
  noStyles = false,
  onDropIn,
  disabled,
  ...otherProps
}: DropInContainerProps) => {
  const { connectors, actions, query } = useEditor();
  const { selection } = useSelection();
  const mutations = useMutations();
  const appearance = usePaletteAppearence();
  const dispatch = useDispatch();
  const activeHotspot = useActiveTooltip();
  const { viewportContainerEl } = useViewportDom();
  const eventing = useEventing();

  const onCreate = (node: NodeTree) => {
    if (disabled) {
      return;
    }

    eventing.track(`editor.useComponent.${id.split(' ').join('-')}`);
    actions.setEventNodes(EventTypes.selected, node.rootNodeId);
    if (onDropIn) {
      onDropIn(node);
    }
  };
  const closeTab = () => dispatch(setPaletteTab(undefined));

  const onClick = () => {
    if (disabled) {
      return;
    }

    const insertAtNode = selection.last || activeHotspot?.id || ROOT_NODE;

    if (contentDocument) {
      const [newTree] = mutations.insertContentDocument(contentDocument, insertAtNode);
      onCreate(newTree);
    } else if (dropIn) {
      const newNodeTree = query.parseReactElement(dropIn).toNodeTree();
      mutations.insertNodeTrees([newNodeTree], insertAtNode);
      onCreate(newNodeTree);
    }
    // scroll the viewport all the way to the bottom
    if (!selection.last) {
      viewportContainerEl?.scrollBy(0, viewportContainerEl?.scrollHeight);
    }

    // close the palette
    if (appearance !== PaletteAppearance.PINNED) {
      closeTab();
    }
  };

  return (
    <Container
      data-cy="dropin"
      data-dropin={id}
      onClick={onClick}
      ref={(ref: any) => {
        if (disabled) {
          return;
        }
        connectors.create(ref, dropIn || <Fragment />, { onCreate: dropIn ? onCreate : onClick });
      }}
      noStyles={noStyles}
      disabled={disabled}
      {...otherProps}
    >
      {children}
    </Container>
  );
};

export default DropInContainer;
